// ⚠️ If you modify the styling of this component and you're using the SectionListings component in your marketplace (featured listings)
// please reflect those changes in the calculateCarouselHeight function in SectionListings.js to avoid layout issues
import React from 'react';
import classNames from 'classnames';
import { Button as AButton } from 'antd';
import { HeartOutlined, HeartFilled } from '@ant-design/icons';

import { useConfiguration } from '../../context/configurationContext';

import { useIntl } from '../../util/reactIntl';
import { requireListingImage } from '../../util/configHelpers';
import { GRID_STYLE_SQUARE } from '../../util/types';
import { createLazyLoader, lazyLoadWithDimensions } from '../../util/uiHelpers';
import { createSlug } from '../../util/urlHelpers';

import {
  AspectRatioWrapperMaybe,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
} from '../../components';

import { getListingCardTranslations } from './ListingCard.helpers';

import css from './ListingCard.module.css';

const lazyLoadWhenVisible = createLazyLoader({ withDimensions: false });
const LazyMasonryImage = lazyLoadWhenVisible(ResponsiveImage, { loadAfterInitialRendering: 3000 });
const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It either renders the first image from the listing's images array with lazy loading,
 * or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 * @param {Object} props
 * @param {Object} props.listing listing entity with image data
 * @param {Function?} props.setActivePropsMaybe mouse enter/leave handlers for map highlighting
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {boolean} props.showListingImage whether to show actual listing image or not
 * @param {Object?} props.style the background color for the listing card with no image
 * @returns {JSX.Element} listing image with fixed aspect ratio or fallback preview
 */
const ListingCardImage = props => {
  const {
    listing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    aspectRatioClassName,
    lazyLoadImage,
    isSquareLayout,
    isCreativeProfile,
  } = props;

  const authorProfileImage = listing?.author?.profileImage || null;
  const firstImage = isCreativeProfile ? authorProfileImage : listing?.images?.[0] || null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k =>
        k.startsWith(isSquareLayout ? variantPrefix : 'scaled-medium')
      )
    : [];

  const aspectRatioClass = aspectRatioClassName || css.aspectRatioWrapper;
  const ImageComponent = lazyLoadImage ? LazyImage : ResponsiveImage;
  const MasonryComponent = lazyLoadImage ? LazyMasonryImage : ResponsiveImage;
  const imageProps = {
    alt: title,
    image: firstImage,
    variants: variants,
    sizes: renderSizes,
  };

  return (
    <AspectRatioWrapperMaybe
      className={aspectRatioClass}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
      isSquareLayout={isSquareLayout}
    >
      {isSquareLayout ? (
        <ImageComponent rootClassName={css.rootForImage} {...imageProps} />
      ) : (
        <div className={css.masonryImageWrapper}>
          <MasonryComponent {...imageProps} />
        </div>
      )}
    </AspectRatioWrapperMaybe>
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.aspectRatioClassName custom className for AspectRatioWrapper component
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {string?} props.isFavorite is it a currentUser's favorite
 * @param {Function?} props.onToggleFavorites
 * @param {Function?} props.setActiveListing
 * @param {number?} props.imageAreaMaxHeight max image band height (px); tall images clip, centered
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    aspectRatioClassName,
    darkMode,
    listing,
    renderSizes,
    setActiveListing,
    hidePrice,
    isFavorite,
    onToggleFavorites,
    gridLayout,
    showAuthorInfo = true,
    lazyLoadImage = true,
    imageAreaMaxHeight,
  } = props;

  const translations = getListingCardTranslations(listing, hidePrice, config, intl);
  const {
    titlePlain,
    titleFormatted,
    cardAriaLabel,
    showPrice,
    priceTooltip,
    priceMessage,
    authorName,
    isCreativeProfile,
  } = translations;

  const slug = createSlug(titlePlain);
  const classes = classNames(rootClassName || css.root, className);

  const id = listing?.id?.uuid;
  const { publicData } = listing?.attributes || {};
  const { listingType, cardStyle } = publicData || {};
  const validListingTypes = config.listing.listingTypes || [];
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  // Render the listing image only if listing images are enabled in the listing type
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const isSquareLayout = gridLayout === GRID_STYLE_SQUARE || isCreativeProfile;

  // Sets the listing as active in the search map when hovered (if the search map is enabled)
  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(listing?.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const toggleFavorites = event => {
    event.preventDefault();
    event.stopPropagation();
    onToggleFavorites?.(isFavorite);
  };
  const imageAreaShell = (inner, maxHeight) =>
    maxHeight != null ? (
      <div className={css.imageAreaHeightCap} style={{ maxHeight }}>
        {inner}
      </div>
    ) : (
      inner
    );

  const favoriteButton =
    onToggleFavorites &&
    (isFavorite ? (
      <AButton
        type="text"
        icon={<HeartFilled style={{ fontSize: '30px' }} />}
        onClick={toggleFavorites}
        className={css.favoriteButton}
      />
    ) : (
      <AButton
        type="text"
        icon={<HeartOutlined style={{ fontSize: '30px' }} />}
        onClick={toggleFavorites}
        className={css.favoriteButton}
      />
    ));

  return (
    <NamedLink
      className={classes}
      name="ListingPage"
      params={{ id, slug }}
      ariaLabel={cardAriaLabel}
    >
      <div className={css.imageArea}>
        {imageAreaShell(
          showListingImage ? (
            <ListingCardImage
              renderSizes={renderSizes}
              title={titlePlain}
              listing={listing}
              setActivePropsMaybe={setActivePropsMaybe}
              aspectWidth={aspectWidth}
              aspectHeight={aspectHeight}
              variantPrefix={variantPrefix}
              aspectRatioClassName={aspectRatioClassName}
              lazyLoadImage={lazyLoadImage}
              isSquareLayout={isSquareLayout}
              isCreativeProfile={isCreativeProfile}
            />
          ) : (
            <ListingCardThumbnail
              style={cardStyle}
              listingTitle={titlePlain}
              className={aspectRatioClassName}
              width={aspectWidth}
              height={aspectHeight}
              setActivePropsMaybe={setActivePropsMaybe}
              isSquareLayout={isSquareLayout}
            />
          ),
          imageAreaMaxHeight
        )}
        {favoriteButton ? (
          <div className={css.menubarWrapper}>
            <div className={css.menubarGradient} />
            <div className={css.menubar}>{favoriteButton}</div>
          </div>
        ) : null}
      </div>
      <div className={css.info}>
        {showPrice ? (
          <div className={css.price} title={priceTooltip}>
            {priceMessage}
          </div>
        ) : null}
        {isCreativeProfile && (
          <div className={css.mainInfo}>
            {showListingImage && (
              <div className={classNames(css.title, { [css.lightText]: darkMode })}>
                {titleFormatted}
              </div>
            )}
            {showAuthorInfo && (
              <div className={classNames(css.authorInfo, { [css.lightText]: darkMode })}>
                {authorName}
              </div>
            )}
          </div>
        )}
      </div>
    </NamedLink>
  );
};

export default ListingCard;
